import React, { useEffect } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useDispatch } from 'react-redux';
import {
  DEFAULT_EXTRA_ITEM,
  IFabricationOrderExtraItem
} from '../../../../../../store/ducks/fabrication-purchase-order/types';
import { loadSteelTypeDiameters } from '../../../../../../store/ducks/fabrication/actions';
import {
  IDiameter,
  IPieceTypesState,
  ISteelTypesState
} from '../../../../../../store/ducks/fabrication/types';
import RemoveItemButton from '../../../../../../components/RemoveItemButton/Facelift';
import { Table, Stepper } from '@gerdau/hefesto-mais-web';
import { Select, TextField, Tooltip } from '@gerdau/hefesto';
import { TSelectOptionProps } from '@gerdau/hefesto/Select/Select.types';
import { FabricationPurchaseOrderService } from '../../../../../../services/FabricationPurchaseOrder';
import { InfoIcon } from '../../../../../../components/InfoTooltip/styles';
import { MeasuresContainer } from './styles';

interface IExtraItemTableProps {
  extraItens: IFabricationOrderExtraItem[];
  updateItemState(item: IFabricationOrderExtraItem): void;
  pieceTypesState?: IPieceTypesState;
  steelTypesState?: ISteelTypesState;
  onRemoveItem?(index: number): void;
  isTheOnlyItem?: boolean;
}

const ExtraItemTable: React.FC<IExtraItemTableProps> = ({
  extraItens,
  updateItemState,
  pieceTypesState,
  steelTypesState,
  onRemoveItem,
  isTheOnlyItem
}) => {
  const dispatch = useDispatch();
  const dataSource = extraItens.map((item, idx) => ({ ...item, key: idx }));
  const maxMeasureValue = 1200;

  const getSelectedPieceType = (pieceTypeId?: number) => {
    const selected = pieceTypesState?.pieceTypes.find(
      (st) => st.id === pieceTypeId
    );

    return selected;
  };

  const canShowRemoveButton = (item: IFabricationOrderExtraItem) => {
    const { pieceTypeId, steelTypeId } = item;
    return !isTheOnlyItem || pieceTypeId || steelTypeId;
  };

  const onSelectPieceType = (
    pieceTypeId: number,
    index: number,
    item: IFabricationOrderExtraItem
  ) => {
    const selected = getSelectedPieceType(pieceTypeId);
    if (!selected) {
      return;
    }

    const updatedState = {
      ...item,
      ...DEFAULT_EXTRA_ITEM,
      pieceTypeId,
      key: index,
      measureB: !selected?.measureB ? undefined : item.measureB,
      measureC: !selected?.measureC ? undefined : item.measureC
    };

    updateItemState(updatedState);
  };
  useEffect(() => {
    if (extraItens.length && !steelTypesState?.loading) {
      const ids = extraItens.map((e) => e.steelTypeId);

      ids.forEach((steelTypeId) => {
        const selected = steelTypesState?.steelTypes.find(
          (st) => st.id === steelTypeId
        );
        if (steelTypeId && selected && !selected.diametersState) {
          dispatch(loadSteelTypeDiameters(steelTypeId));
        }
      });
    }
  }, [steelTypesState?.loading]);

  const diameterIsLoading = (steelTypeId?: number) => {
    const selected = steelTypesState?.steelTypes.find(
      (st) => st.id === steelTypeId
    );
    return Boolean(selected?.diametersState?.loading);
  };

  const getSelectedSteelType = (steelTypeId?: number) => {
    const selected = steelTypesState?.steelTypes.find(
      (st) => st.id === steelTypeId
    );
    if (steelTypeId && selected && !selected.diametersState) {
      dispatch(loadSteelTypeDiameters(steelTypeId));
    }
    return selected;
  };

  const onSelectSteelType = (
    steelTypeId: number,
    index: number,
    item: IFabricationOrderExtraItem
  ) => {
    const updatedState = {
      ...item,
      steelTypeId,
      key: index,
      diameterId: undefined,
      weight: undefined
    };

    updateItemState(updatedState);
    getSelectedSteelType(steelTypeId);
  };

  const updateState = (
    field: string,
    newValue: number | string,
    updatedItem: IFabricationOrderExtraItem
  ) => {
    //Em alguns casos o valor é número e em outros é string
    const removeDotComma = Number(String(newValue).replace(/[.,]/g, ''));

    const updatedExtraItem = {
      ...updatedItem,
      [field]: removeDotComma
    };

    const { pieceTypeId, amount, measureA, measureB, measureC } =
      updatedExtraItem;
    const selectedSteel = steelTypesState?.steelTypes.find(
      (st) => st.id === updatedExtraItem.steelTypeId
    );

    const selectedDiameter = selectedSteel?.diametersState?.diameters.find(
      (d) => d.id === updatedExtraItem.diameterId
    ) as IDiameter;

    const pieceType = getSelectedPieceType(updatedExtraItem?.pieceTypeId);

    const isInvalidMeasureA =
      pieceType?.measureA &&
      measureA !== undefined &&
      measureA > maxMeasureValue;

    const isInvalidMeasureB =
      pieceType?.measureB &&
      measureB !== undefined &&
      measureB > maxMeasureValue;

    const isInvalidMeasureC =
      pieceType?.measureC &&
      measureC !== undefined &&
      measureC > maxMeasureValue;

    const shouldUpdateWeight =
      pieceTypeId &&
      amount &&
      selectedDiameter &&
      measureA &&
      !isInvalidMeasureA &&
      !isInvalidMeasureB &&
      !isInvalidMeasureC;

    const weight =
      (shouldUpdateWeight &&
        pieceTypeId &&
        FabricationPurchaseOrderService.calculateExtraItemWeight(
          pieceTypeId,
          Number(amount),
          selectedDiameter,
          Number(measureA || 0),
          measureB ? Number(measureB) : 0,
          measureC ? Number(measureC) : 0
        )) ||
      0;

    updateItemState({
      ...updatedExtraItem,
      ...(shouldUpdateWeight && { weight })
    });
  };

  const getExtraItensOptions = (): TSelectOptionProps[] => {
    return (
      pieceTypesState?.pieceTypes.map((pieceType) => ({
        value: pieceType?.id.toString(),
        label: pieceType?.description,
        ...pieceType
      })) || []
    );
  };

  const getExtraItenSteelTypeOptions = (): TSelectOptionProps[] => {
    return (
      steelTypesState?.steelTypes.map((steelType) => {
        return {
          value: steelType?.id.toString(),
          label: steelType?.description
        };
      }) || []
    );
  };

  const getExtraItensDiameterOptions = (
    steelTypeId: number
  ): TSelectOptionProps[] => {
    const selected = steelTypesState?.steelTypes.find(
      (st) => st.id === steelTypeId
    );

    if (!selected?.diametersState) {
      return [];
    }

    const options: TSelectOptionProps[] =
      selected?.diametersState?.diameters.map((e) => ({
        value: e.id.toString(),
        label: e.size.toString()
      }));
    return options;
  };

  const checkAvaliableMeasure = (
    measureType: 'measureA' | 'measureB' | 'measureC',
    pieceTypeId: number
  ) => {
    return pieceTypesState?.pieceTypes.find(
      (piece) => piece.id === pieceTypeId
    )?.[measureType];
  };

  const getColumns = () => {
    const columns = [
      {
        title: 'Tipo de peça',
        dataIndex: '',
        render: (item: IFabricationOrderExtraItem) => (
          <>
            {(!pieceTypesState ||
              (pieceTypesState && pieceTypesState.loading) ||
              (pieceTypesState && pieceTypesState.error)) && (
              <Skeleton height="1.75rem" />
            )}

            {pieceTypesState &&
              !pieceTypesState.loading &&
              !pieceTypesState.error && (
                <Select
                  label={''}
                  name={'pieceType'}
                  hideLabel
                  value={item.pieceTypeId?.toString()}
                  options={getExtraItensOptions()}
                  onSelect={(pieceTypeId) => {
                    onSelectPieceType(
                      Number(pieceTypeId),
                      item.key as number,
                      item
                    );
                  }}
                />
              )}
          </>
        )
      },
      {
        title: 'Tipo de aço',
        dataIndex: '',
        render: (item: IFabricationOrderExtraItem) => (
          <>
            {(!steelTypesState ||
              (steelTypesState && steelTypesState.loading) ||
              (steelTypesState && steelTypesState.error)) && (
              <Skeleton height="1.75rem" />
            )}

            {steelTypesState &&
              !steelTypesState.loading &&
              !steelTypesState.error && (
                <Select
                  label={''}
                  name={'steelType'}
                  hideLabel
                  value={item.steelTypeId?.toString() || ''}
                  options={getExtraItenSteelTypeOptions()}
                  onSelect={(steelTypeId) => {
                    onSelectSteelType(
                      Number(steelTypeId),
                      item.key as number,
                      item
                    );
                  }}
                />
              )}
          </>
        )
      },
      {
        title: 'Bitola',
        dataIndex: '',
        render: (item: IFabricationOrderExtraItem) => {
          return (
            <>
              {!steelTypesState ||
                (steelTypesState && steelTypesState.loading) ||
                (steelTypesState && steelTypesState.error) ||
                (diameterIsLoading(item.steelTypeId) && (
                  <Skeleton width="7rem" height="2rem" />
                ))}
              {steelTypesState &&
                !steelTypesState.loading &&
                !steelTypesState.error &&
                !diameterIsLoading(item.steelTypeId) && (
                  <Select
                    label={''}
                    name={'diameter'}
                    hideLabel
                    value={item.diameterId?.toString()}
                    options={
                      item.steelTypeId
                        ? getExtraItensDiameterOptions(item.steelTypeId)
                        : []
                    }
                    onSelect={(value) =>
                      updateState('diameterId', Number(value), item)
                    }
                    disabled={
                      !item.steelTypeId || diameterIsLoading(item.steelTypeId)
                    }
                  />
                )}
            </>
          );
        }
      },
      {
        title: (
          <MeasuresContainer>
            <span>Medida a</span>
            <Tooltip title="Medida A não pode ser maior do que 1200cm">
              <InfoIcon />
            </Tooltip>
          </MeasuresContainer>
        ),
        dataIndex: '',
        render: (item: IFabricationOrderExtraItem) => (
          <Stepper
            data-testid="stepper-measureA"
            label=""
            name="measure-a-input"
            disabled={
              !item.pieceTypeId ||
              !checkAvaliableMeasure('measureA', item.pieceTypeId)
            }
            value={item.measureA ?? 0}
            onChange={(e) => updateState('measureA', e.target.value, item)}
            maxLength={1200}
            minLength={0}
          />
        )
      },
      {
        title: (
          <MeasuresContainer>
            <span>Medida b</span>
            <Tooltip title="Medida B não pode ser maior do que 1200cm">
              <InfoIcon />
            </Tooltip>
          </MeasuresContainer>
        ),
        dataIndex: '',
        render: (item: IFabricationOrderExtraItem) => (
          <Stepper
            data-testid="stepper-measureB"
            label=""
            name="measure-b-input"
            disabled={
              !item.pieceTypeId ||
              !checkAvaliableMeasure('measureB', item.pieceTypeId)
            }
            value={item.measureB ?? 0}
            onChange={(e) => updateState('measureB', e.target.value, item)}
            maxLength={1200}
            minLength={0}
          />
        )
      },
      {
        title: (
          <MeasuresContainer>
            <span>Medida c</span>
            <Tooltip title="Medida C não pode ser maior do que 1200cm">
              <InfoIcon />
            </Tooltip>
          </MeasuresContainer>
        ),
        dataIndex: '',
        render: (item: IFabricationOrderExtraItem) => (
          <Stepper
            data-testid="stepper-measureC"
            label=""
            name="measure-b-input"
            disabled={
              !item.pieceTypeId ||
              !checkAvaliableMeasure('measureC', item.pieceTypeId)
            }
            value={item.measureC ?? 0}
            onChange={(e) => updateState('measureC', e.target.value, item)}
            maxLength={1200}
            minLength={0}
          />
        )
      },
      {
        title: 'Quantidade',
        dataIndex: '',
        render: (item: IFabricationOrderExtraItem) => (
          <Stepper
            data-testid="stepper-amount"
            label=""
            name="amount-input"
            disabled={!item.pieceTypeId}
            value={item?.amount ?? 0}
            onChange={(e) =>
              //  disabling decimal numbers
              /^-?\d+$/.test(e.target.value) || e.target.value === ''
                ? updateState('amount', e.target.value, item)
                : item?.amount ?? 0
            }
            minLength={0}
          />
        )
      },
      {
        title: 'Peso da peça',
        dataIndex: '',
        render: (item: IFabricationOrderExtraItem) => (
          <TextField
            data-testid="piece-weight"
            name="pieceWeight"
            label=""
            type="number"
            placeholder="Em kg"
            disabled={true}
            value={item.weight}
          />
        )
      },
      {
        title: '',
        dataIndex: '',
        align: 'center' as const,
        render: (item: IFabricationOrderExtraItem) => (
          <>
            {onRemoveItem !== undefined && canShowRemoveButton(item) && (
              <>
                <RemoveItemButton
                  onRemoveItem={() =>
                    item.key?.toString() && onRemoveItem(item.key)
                  }
                />
              </>
            )}
          </>
        )
      }
    ];
    return columns;
  };

  return <Table columns={getColumns()} dataSource={dataSource}></Table>;
};

export default ExtraItemTable;
